import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadingScreen } from '../components/LoadingScreen';
import { StatusBadge } from '../components/StatusBadge';
import { errorMessage } from '../lib/errors';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/text';
import type { ContentRelease, RiddleImport } from '../lib/types';

interface Stats {
  total: number;
  draft: number;
  review: number;
  approved: number;
  published: number;
}

export function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [latestImport, setLatestImport] = useState<RiddleImport | null>(null);
  const [latestRelease, setLatestRelease] = useState<ContentRelease | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const statuses = ['draft', 'needs_review', 'approved', 'published'] as const;
        const counts = await Promise.all(
          statuses.map((status) =>
            supabase.from('riddles').select('id', { count: 'exact', head: true }).eq('status', status),
          ),
        );
        const totalResult = await supabase
          .from('riddles')
          .select('id', { count: 'exact', head: true });
        for (const result of [...counts, totalResult]) {
          if (result.error) throw result.error;
        }

        const [importResult, releaseResult] = await Promise.all([
          supabase.from('riddle_imports').select('*').order('created_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('content_releases').select('*').order('created_at', { ascending: false }).limit(1).maybeSingle(),
        ]);
        if (importResult.error) throw importResult.error;
        if (releaseResult.error) throw releaseResult.error;

        setStats({
          total: totalResult.count ?? 0,
          draft: counts[0].count ?? 0,
          review: counts[1].count ?? 0,
          approved: counts[2].count ?? 0,
          published: counts[3].count ?? 0,
        });
        setLatestImport((importResult.data as RiddleImport | null) ?? null);
        setLatestRelease((releaseResult.data as ContentRelease | null) ?? null);
      } catch (caught) {
        setError(errorMessage(caught));
      }
    };

    void load();
  }, []);

  if (!stats && !error) return <LoadingScreen label="Chargement du tableau de bord…" />;

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Vue d’ensemble</p>
          <h1>Tableau de bord</h1>
          <p>État du catalogue éditorial et des dernières opérations de production.</p>
        </div>
        <div className="header-actions">
          <Link className="button secondary" to="/imports">Importer</Link>
          <Link className="button primary" to="/riddles/new">Nouvelle énigme</Link>
        </div>
      </header>

      {error && <div className="alert error">{error}</div>}

      {stats && (
        <section className="stat-grid">
          <article className="stat-card featured">
            <span>Catalogue total</span>
            <strong>{stats.total.toLocaleString('fr-FR')}</strong>
            <small>énigmes enregistrées</small>
          </article>
          <article className="stat-card">
            <span>Brouillons</span>
            <strong>{stats.draft.toLocaleString('fr-FR')}</strong>
            <small>à compléter</small>
          </article>
          <article className="stat-card">
            <span>À vérifier</span>
            <strong>{stats.review.toLocaleString('fr-FR')}</strong>
            <small>en attente de revue</small>
          </article>
          <article className="stat-card">
            <span>Approuvées</span>
            <strong>{stats.approved.toLocaleString('fr-FR')}</strong>
            <small>prêtes à publier</small>
          </article>
          <article className="stat-card">
            <span>Publiées</span>
            <strong>{stats.published.toLocaleString('fr-FR')}</strong>
            <small>disponibles aux joueurs</small>
          </article>
        </section>
      )}

      <section className="two-column-grid">
        <article className="panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Dernier import</p>
              <h2>{latestImport?.filename ?? 'Aucun import'}</h2>
            </div>
            {latestImport && <StatusBadge status={latestImport.status} />}
          </div>
          {latestImport ? (
            <div className="detail-list">
              <div><span>Date</span><strong>{formatDate(latestImport.created_at)}</strong></div>
              <div><span>Total</span><strong>{latestImport.total_count.toLocaleString('fr-FR')}</strong></div>
              <div><span>Valides</span><strong>{latestImport.valid_count.toLocaleString('fr-FR')}</strong></div>
              <div><span>Erreurs</span><strong>{latestImport.error_count.toLocaleString('fr-FR')}</strong></div>
            </div>
          ) : (
            <p className="muted">Importe ton premier fichier JSON depuis la page Imports.</p>
          )}
          <Link className="text-link" to="/imports">Voir les imports →</Link>
        </article>

        <article className="panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Dernière publication</p>
              <h2>{latestRelease?.name ?? 'Aucune publication'}</h2>
            </div>
            {latestRelease && <StatusBadge status={latestRelease.status} />}
          </div>
          {latestRelease ? (
            <div className="detail-list">
              <div><span>Version</span><strong>#{latestRelease.id}</strong></div>
              <div><span>Schéma</span><strong>v{latestRelease.schema_version}</strong></div>
              <div><span>Créée</span><strong>{formatDate(latestRelease.created_at)}</strong></div>
              <div><span>Publiée</span><strong>{formatDate(latestRelease.published_at)}</strong></div>
            </div>
          ) : (
            <p className="muted">Les énigmes approuvées pourront être publiées dans un pack SQLite.</p>
          )}
          <Link className="text-link" to="/releases">Gérer les publications →</Link>
        </article>
      </section>
    </div>
  );
}
